import React, { useEffect, useState, useCallback } from 'react';
import { Search, Send, Check, X, AlertTriangle, Clock, Mail, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import NombreCliente from '../../components/NombreCliente';
import { fecha, Title, Kpi, Badge, Empty } from './ui';

/* Todo lo que la plataforma le mandó por mail a un cliente, y lo que espera el sí.
   Los avisos de un servicio con el aviso automático apagado caen acá como
   pendientes: se ven, se revisan y recién entonces salen. */

const ESTADOS = {
  pendiente: { label: 'Esperando tu ok', color: '#d2930c', bg: 'rgba(210,147,12,0.15)', icono: Clock },
  enviado: { label: 'Enviado', color: '#12a37a', bg: 'rgba(18,163,122,0.15)', icono: Check },
  error: { label: 'No salió', color: '#d9455f', bg: 'rgba(217,69,95,0.15)', icono: AlertTriangle },
  cancelado: { label: 'Cancelado', color: 'var(--text-muted)', bg: 'transparent', icono: X },
};
const TIPOS = {
  cobro: 'Cobro de abono', renovacion: 'Renovación', reporte: 'Reporte',
  recordatorio: 'Recordatorio de pago', pago: 'Pago recibido', proyecto: 'Avance de proyecto',
  minuta: 'Minuta', propuesta: 'Propuesta', otro: 'Otro',
};

export default function Envios() {
  const [data, setData] = useState(null);
  const [filtros, setFiltros] = useState({ estado: '', tipo: '', buscar: '' });
  const [abierto, setAbierto] = useState(null);
  const [trabajando, setTrabajando] = useState(null);

  const { estado, tipo, buscar } = filtros;
  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (estado) q.set('estado', estado);
    if (tipo) q.set('tipo', tipo);
    if (buscar) q.set('buscar', buscar);
    return api.get('/agency/envios?' + q.toString())
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudo cargar'));
  }, [estado, tipo, buscar]);
  useEffect(() => { const t = setTimeout(load, buscar ? 300 : 0); return () => clearTimeout(t); }, [load, buscar]);

  const accion = async (e, que) => {
    if (que === 'aprobar' && !window.confirm(
      `Se le manda ahora a:\n${e.destinatarios || '—'}\n\nAsunto: ${e.asunto}`)) return;
    setTrabajando(e.id);
    try {
      const { data: r } = await api.post(`/agency/envios/${e.id}/${que}`);
      toast.success(que === 'aprobar' ? 'Enviado a ' + (r.destinatarios || []).join(', ') : 'Cancelado');
      setAbierto(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo'); }
    finally { setTrabajando(null); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: 8 };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const r = data?.resumen || {};

  return (
    <div>
      <Title right={<button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /> Actualizar</button>}>
        Envíos
      </Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Kpi label="Esperando tu ok" value={r.pendientes ?? '—'} icono={Clock}
          color={r.pendientes ? '#d2930c' : undefined}
          sub={r.pendientes ? 'No se mandaron todavía' : 'Nada pendiente'}
          onClick={() => setFiltros({ ...filtros, estado: 'pendiente' })} />
        <Kpi label="Enviados · 30 días" value={r.enviados_mes ?? '—'} icono={Send}
          onClick={() => setFiltros({ ...filtros, estado: 'enviado' })} />
        <Kpi label="No salieron" value={r.errores ?? '—'} icono={AlertTriangle}
          color={r.errores ? '#d9455f' : undefined}
          sub={r.errores ? 'Revisá el motivo' : 'Sin fallas'}
          onClick={() => setFiltros({ ...filtros, estado: 'error' })} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', gap: 10, padding: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={filtros.buscar} onChange={e => setFiltros({ ...filtros, buscar: e.target.value })}
              placeholder="Buscar por cliente, asunto o mail" style={{ paddingLeft: 34 }} />
          </div>
          <select value={filtros.estado} onChange={e => setFiltros({ ...filtros, estado: e.target.value })} style={{ width: 'auto' }}>
            <option value="">Todos los estados</option>
            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filtros.tipo} onChange={e => setFiltros({ ...filtros, tipo: e.target.value })} style={{ width: 'auto' }}>
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="table-container" style={{ border: 0, borderRadius: 0 }}>
          <table>
            <thead><tr>
              <th style={th}>Cuándo</th><th style={th}>Cliente</th><th style={th}>Asunto</th>
              <th style={th}>Tipo</th><th style={th}>Para</th><th style={th}>Estado</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {!data ? <tr><td colSpan={7} style={{ ...td, color: 'var(--text-muted)' }}>Cargando...</td></tr>
                : data.envios.length === 0 ? <tr><td colSpan={7} style={td}><Empty>No hay envíos con ese filtro</Empty></td></tr>
                : data.envios.map(e => {
                  const es = ESTADOS[e.estado] || ESTADOS.otro;
                  return (
                    <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setAbierto(e)}>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>{fecha(e.enviado_at || e.created_at)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{e.cliente ? <NombreCliente nombre={e.cliente} logo={e.logo_url} /> : '—'}</td>
                      <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.asunto}</td>
                      <td style={{ ...td, color: 'var(--text-secondary)', fontSize: 12 }}>
                        {TIPOS[e.tipo] || e.tipo}{!e.automatico && <span style={{ color: 'var(--text-muted)' }}> · manual</span>}
                      </td>
                      <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.destinatarios || '—'}</td>
                      <td style={td}><Badge label={es.label} color={es.color} bg={es.bg} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={ev => ev.stopPropagation()}>
                        {e.estado === 'pendiente' && (
                          <>
                            <button className="btn btn-primary btn-sm" disabled={trabajando === e.id} onClick={() => accion(e, 'aprobar')}>
                              <Send size={13} /> Enviar
                            </button>
                            <button className="btn btn-ghost btn-sm" disabled={trabajando === e.id} onClick={() => accion(e, 'cancelar')} title="Descartar">
                              <X size={14} />
                            </button>
                          </>
                        )}
                        {e.estado === 'error' && (
                          <button className="btn btn-secondary btn-sm" disabled={trabajando === e.id} onClick={() => accion(e, 'aprobar')}>
                            <RefreshCw size={13} /> Reintentar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {abierto && (
        <div className="modal-overlay" onClick={() => setAbierto(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2 style={{ marginBottom: 6 }}>{abierto.asunto}</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              <Badge label={(ESTADOS[abierto.estado] || {}).label} color={(ESTADOS[abierto.estado] || {}).color} bg={(ESTADOS[abierto.estado] || {}).bg} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {abierto.cliente || 'Sin cliente'} · {TIPOS[abierto.tipo] || abierto.tipo} · {fecha(abierto.enviado_at || abierto.created_at)}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 14 }}>
              <Mail size={15} style={{ marginTop: 2, color: 'var(--text-muted)', flexShrink: 0 }} />
              <span style={{ wordBreak: 'break-word' }}>{abierto.destinatarios || 'Sin destinatarios'}</span>
            </div>

            {abierto.detalle && (
              <div style={{ background: 'rgba(217,69,95,0.10)', border: '1px solid rgba(217,69,95,0.3)', color: '#d9455f',
                borderRadius: 10, padding: '10px 12px', fontSize: 12.5, marginBottom: 14 }}>{abierto.detalle}</div>
            )}

            <div style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: 14, fontSize: 13,
              whiteSpace: 'pre-line', lineHeight: 1.65, color: 'var(--text-secondary)', maxHeight: 260, overflowY: 'auto' }}>
              {abierto.cuerpo || 'Sin cuerpo'}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setAbierto(null)}>Cerrar</button>
              {abierto.estado === 'pendiente' && (
                <>
                  <button className="btn btn-ghost" onClick={() => accion(abierto, 'cancelar')}>Descartar</button>
                  <button className="btn btn-primary" onClick={() => accion(abierto, 'aprobar')}><Send size={15} /> Enviar ahora</button>
                </>
              )}
              {abierto.estado === 'error' && (
                <button className="btn btn-primary" onClick={() => accion(abierto, 'aprobar')}><RefreshCw size={15} /> Reintentar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
